import { faTrashAlt, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import React, { Fragment, useState } from 'react';
import { getConstructionPhases, updateConstructionPhases } from '@/services/construction-phase';
import { useAsyncState } from '@/utils/hooks';
import SettingsSection from '../SettingsSection';
import theme from './ProjectSettingsConstructionPhases.module.scss';
import mainTheme from '../ProjectSettings.module.scss';
import { ConstructionPhase, OptionalId } from '@/types';
import { getResidentialUnitGroupsByProjectId } from '@/services/residential-unit-groups';
import { LoadingIndicator, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import { Tooltip } from '@/components/Tooltip';
import { classnames } from '@/utils/html';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';

interface ProjectSettingsConstructionPhasesProps {
    projectId: number;
}

const ProjectSettingsConstructionPhases = ({ projectId }: ProjectSettingsConstructionPhasesProps) => {
    const [residentialUnitGroups] = useAsyncState(() => getResidentialUnitGroupsByProjectId(projectId), [projectId], null);
    const [constructionPhases, setConstructionPhases] = useAsyncState<OptionalId<ConstructionPhase>[] | null>(
        () => getConstructionPhases(projectId),
        [projectId],
        null
    );
    const [isSaving, setIsSaving] = useState(false);

    if (!constructionPhases || !residentialUnitGroups) {
        return <LoadingIndicator />;
    }
    const onSubmit = async () => {
        setIsSaving(true);
        await updateConstructionPhases(projectId, constructionPhases);
        setIsSaving(false);
        Toast.success(<Trans>Saved construction phases</Trans>);
    };
    return (
        <>
            <AlertBanner type="warning">
                <Trans>
                    Construction Phase is a global object that is shared among all modules. This means that changes made in
                    CM will also affect AM, and vice versa.
                </Trans>
            </AlertBanner>
            <SettingsSection>
                <SettingsSection.Content>
                    <ValidationForm onSubmit={onSubmit}>
                        <div className={theme.gridContainer}>
                            <h5>
                                <Trans>Construction phases</Trans>
                                <InfoIconWithTooltip
                                    content={
                                        <Trans>
                                            Construction phases are only relevant if the project need different landing pages
                                            for the units. For instance if there are several construction phase in the
                                            project with different timelines. After a construction phase is created it must
                                            also be added to one or several building groups. Navigate to units and edit the
                                            relevant unit groups.
                                        </Trans>
                                    }
                                />
                            </h5>

                            {constructionPhases.map((cp, index) => {
                                const cannotBeDeleted = residentialUnitGroups.some((x) => x.constructionPhaseId === cp.id);
                                return (
                                    <div key={index} className={theme.row}>
                                        <ValidatingInput
                                            maxLength={250}
                                            type="text"
                                            required
                                            className="form-control"
                                            placeholder={t`Construction phase name`}
                                            value={cp.name || ''}
                                            onChange={(e) =>
                                                setConstructionPhases(
                                                    constructionPhases.map((_pf, i) =>
                                                        i === index ? { ..._pf, name: e.target.value } : _pf
                                                    )
                                                )
                                            }
                                        />
                                        <Tooltip
                                            content={
                                                cannotBeDeleted && (
                                                    <Trans>
                                                        Can not be deleted. This construction phase is connected to a
                                                        residential unit group.
                                                    </Trans>
                                                )
                                            }>
                                            <span className={classnames(mainTheme.buttonWrapper, theme.deleteButton)}>
                                                <Button
                                                    disabled={cannotBeDeleted}
                                                    variant="danger"
                                                    className={mainTheme.trashButton}
                                                    onClick={() =>
                                                        setConstructionPhases(
                                                            constructionPhases.filter((e, i) => i !== index)
                                                        )
                                                    }>
                                                    <FontAwesomeIcon size="lg" icon={faTrashAlt} />
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </div>
                                );
                            })}
                        </div>

                        <ValidationSummary />
                        <ButtonBar>
                            <Button
                                icon={faPlus}
                                variant="tertiary"
                                onClick={() => setConstructionPhases(constructionPhases.concat([{ name: '', projectId }]))}>
                                <Trans>Add construction phase</Trans>
                            </Button>
                            <ButtonBar.Right>
                                <Button className={mainTheme.saveButton} type="submit" variant="primary" loading={isSaving}>
                                    <Trans>Save</Trans>
                                </Button>
                            </ButtonBar.Right>
                        </ButtonBar>
                    </ValidationForm>
                </SettingsSection.Content>
            </SettingsSection>
        </>
    );
};

export default ProjectSettingsConstructionPhases;
